import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { ChevronLeft, Download, PencilLine, Share2, SlidersHorizontal } from 'lucide-react';
import { assetConfirm, assetToast, formatDate } from '@xdocs/i18n';
import type { DocumentStatus } from '@xdocs/schemas';
import { Badge, Button, EmptyState, Skeleton, Textarea, cn, useI18n, useToast } from '@xdocs/ui';
import type { BadgeTone } from '@xdocs/ui';
import { useAuth } from '../../auth/AuthProvider.js';
import { JobProgressCard, useJobSnapshot } from '../../features/jobs/index.js';
import { ExportMenu, useVersionHistory } from '../../features/editor/index.js';
import { ShareDialog } from '../../features/sharing/index.js';
import { useDocument } from './useDocument.js';
import { useDocumentsT } from './useDocumentsT.js';
import { useOutlineConfirm } from './useOutlineConfirm.js';
import styles from './DocumentDetailPage.module.css';

const STATUS_TONE: Record<DocumentStatus, BadgeTone> = {
  draft: 'neutral',
  generating: 'info',
  ready: 'success',
  failed: 'error',
};

interface LocationState {
  jobId?: string;
}

/** F15 degraded state: no `outline.get`-shaped WS op exists yet (open
 * issue), so this build can't fetch the proposed outline's chapter content
 * — only its live status/id over `outline-confirm:{documentId}`. Rather
 * than fabricate a "Confirm" that charges for a structure the user never
 * actually reviewed (defeating F15's entire purpose), this offers ONLY the
 * reject path, with the reason clearly explained. */
function OutlineUnavailableNotice({
  documentTitle,
  outlineId,
  onReject,
}: {
  documentTitle: string;
  outlineId: string;
  onReject: (outlineId: string, reason: string | undefined) => Promise<unknown>;
}): ReactElement {
  const { t } = useDocumentsT();
  const { t: tCommon, locale } = useI18n();
  const { push: pushToast } = useToast();
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  // §16.1 (E12): the destructive reject confirmation is built ONLY through
  // the shared `assetConfirm` builder — never hand-concatenated — so it
  // always names the asset AND carries the "can't be undone" suffix.
  const rejectConfirm = assetConfirm(locale, t('documents:outline.reject.confirm'), documentTitle, { destructive: true });

  async function handleReject(): Promise<void> {
    setIsRejecting(true);
    setRejectError(null);
    try {
      await onReject(outlineId, reason.trim().length > 0 ? reason.trim() : undefined);
      pushToast(assetToast(locale, 'success', t('documents:toast.outlineRejected.action'), documentTitle));
    } catch (err) {
      const alert = err as { message?: string };
      setRejectError(alert.message ?? 'errors:code.INTERNAL');
    } finally {
      setIsRejecting(false);
    }
  }

  return (
    <div className={cn(styles['notice'])}>
      <p className={cn(styles['noticeTitle'])}>{t('documents:outline.contentUnavailable.title')}</p>
      <p className={cn(styles['noticeDescription'])}>{t('documents:outline.contentUnavailable.description')}</p>
      {!showReject ? (
        <Button variant="secondary" onClick={() => setShowReject(true)}>
          {t('documents:outline.reject')}
        </Button>
      ) : (
        <div className={cn(styles['rejectForm'])}>
          <Textarea label={t('documents:outline.reject.reasonLabel')} value={reason} onChange={(event) => setReason(event.target.value)} />
          <p className={cn(styles['noticeDescription'])}>{rejectConfirm.message}</p>
          <div className={cn(styles['rejectActions'])}>
            <Button variant="secondary" onClick={() => setShowReject(false)} disabled={isRejecting}>
              {tCommon('common:action.cancel')}
            </Button>
            <Button variant="destructive" state={isRejecting ? 'loading' : 'idle'} onClick={() => void handleReject()}>
              {t('documents:outline.reject.confirm')}
            </Button>
          </div>
          {rejectError && (
            <p role="alert" className={cn(styles['noticeDescription'])}>
              {rejectError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * `/documents/:id` (F1/F14/F15). FEATURES.md's own spec text for this route:
 * "the manuscript reader/editor — outline/chapters tree (left), content
 * (center), and an actions rail (right)". This build renders the header +
 * actions rail (Read/Edit/Themes/Share) and the right-column status stack
 * (live job progress, F15 confirm/reject gate, real version history,
 * deliverables) — the center chapters-tree + manuscript READING pane isn't
 * wired yet even though the data exists (`version.get` via `useCurrentVersion`,
 * already used by `/edit/:docId`): building it needs its own active-chapter
 * state design plus this page's test harness updated to branch by WS op
 * (today's tests use one blanket `document.get`-shaped mock per test) —
 * flagged as the natural next slice rather than rushed in here.
 * Live job progress (hero variant, F14/E11) when a `jobId` is known this
 * session (`state.jobId`, set by `/generate` on `generate.start`), the F15
 * outline-confirm gate over `outline-confirm:{documentId}`, REAL persisted
 * version history (`version.list` via `useVersionHistory` — the earlier
 * "no such op" open issue was stale; `/edit/:docId`'s sidebar already proves
 * it out), and deliverable info from the job's checkpoints. Reconnect-safe:
 * `useDocument`/`useJobSnapshot`/`useVersionHistory` all re-render from the
 * ws-protocol client's own replay/reattach (E20) — this page has no bespoke
 * resume logic.
 */
export function DocumentDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { locale } = useI18n();
  const { t } = useDocumentsT();
  const { userId } = useAuth();
  const documentId = id ?? '';
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const { document, isLoading, isError, error, refetch } = useDocument(documentId);
  const outlineConfirm = useOutlineConfirm(documentId);
  const versionHistory = useVersionHistory(documentId);
  const jobId = (location.state as LocationState | null)?.jobId ?? null;
  const { snapshot: jobSnapshot } = useJobSnapshot(jobId);

  if (!id) {
    return <EmptyState kind="error" title={t('documents:detail.notFound.title')} />;
  }

  if (isLoading) {
    return (
      <div className={cn(styles['page'])} aria-busy="true">
        <Skeleton shape="text-line" width="40%" />
        <Skeleton shape="card" count={2} />
      </div>
    );
  }

  if (isError) {
    if (error?.code === 'NOT_FOUND') {
      return (
        <EmptyState
          kind="error"
          title={t('documents:detail.notFound.title')}
          action={{ label: t('documents:detail.notFound.cta'), onClick: () => void navigate('/documents') }}
          mascotAlt=""
        />
      );
    }
    return (
      <EmptyState kind="error" title={t('documents:detail.error.title')} action={{ label: t('documents:detail.error.retry'), onClick: refetch }} />
    );
  }

  if (!document) return <EmptyState kind="error" title={t('documents:detail.notFound.title')} />;

  const isOwner = document.ownerId === userId;
  const artifactCheckpoints = (jobSnapshot?.checkpoints ?? []).filter((cp) => cp.artifactFileId !== null);
  // Same field `/edit/:docId` (`EditorPage`) and `/style/:docId` (`StylePage`)
  // key off for "the current version" — not `versionHistory.versions[0]`,
  // which is a separate read (`version.list`) that could in principle race
  // or paginate differently from the document row's own pointer.
  const currentVersionId = document.currentVersionId;
  const lastJobDone = jobSnapshot?.job.status === 'succeeded';

  return (
    <div className={cn(styles['page'])}>
      <Link to="/documents" className={cn(styles['backLink'])}>
        <ChevronLeft size={14} aria-hidden="true" />
        {t('documents:detail.back')}
      </Link>

      <div className={cn(styles['header'])}>
        <div className={cn(styles['titleRow'])}>
          <h1 className={cn(styles['title'])}>{document.title}</h1>
          <Badge tone={STATUS_TONE[document.status]}>{t(`documents:status.${document.status}`)}</Badge>
        </div>
        <div className={cn(styles['metaRow'])}>
          <span>{t(`documents:kind.${document.kind}`)}</span>
          <span>{t(`documents:difficulty.${document.difficultyLevel}`)}</span>
          <span>{document.language.toUpperCase()}</span>
        </div>
      </div>

      {isOwner && (
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          documentId={document.id}
          documentTitle={document.title}
        />
      )}

      <div className={cn(styles['layout'])}>
        <div className={cn(styles['main'])}>
          {jobId && <JobProgressCard jobId={jobId} variant="hero" />}

          {!jobId && document.status === 'generating' && (
            <div className={cn(styles['notice'])}>
              <p className={cn(styles['noticeTitle'])}>{t('documents:job.missing.title')}</p>
              <p className={cn(styles['noticeDescription'])}>{t('documents:job.missing.description')}</p>
            </div>
          )}

          {outlineConfirm.status === 'proposed' && outlineConfirm.outlineId && (
            <div className={cn(styles['section'])}>
              <h2 className={cn(styles['sectionTitle'])}>{t('documents:outline.status.proposed')}</h2>
              <OutlineUnavailableNotice documentTitle={document.title} outlineId={outlineConfirm.outlineId} onReject={outlineConfirm.reject} />
            </div>
          )}
          {outlineConfirm.status === 'confirmed' && (
            <p className={cn(styles['emptyNote'])}>{t('documents:outline.status.confirmed')}</p>
          )}
          {outlineConfirm.status === 'rejected' && (
            <p className={cn(styles['emptyNote'])}>{t('documents:outline.status.rejected')}</p>
          )}

          <div className={cn(styles['section'])}>
            <h2 className={cn(styles['sectionTitle'])}>{t('documents:artifacts.title')}</h2>
            {artifactCheckpoints.length === 0 ? (
              <p className={cn(styles['emptyNote'])}>{t('documents:artifacts.empty')}</p>
            ) : (
              <p className={cn(styles['emptyNote'])}>{t('documents:artifacts.produced')}</p>
            )}
          </div>
        </div>

        <aside className={cn(styles['rail'])}>
          <div className={cn(styles['railActions'])}>
            <Link to={`/read/${document.id}`} className={cn(styles['railButton'])}>
              <span className={cn(styles['railIcon'])} aria-hidden="true">
                <Download size={16} />
              </span>
              {t('documents:detail.action.read')}
            </Link>
            {currentVersionId && <ExportMenu documentId={document.id} documentTitle={document.title} versionId={currentVersionId} />}
            {isOwner && (
              <>
                <Link to={`/edit/${document.id}`} className={cn(styles['railButton'])}>
                  <span className={cn(styles['railIcon'])} aria-hidden="true">
                    <PencilLine size={16} />
                  </span>
                  {t('documents:detail.action.edit')}
                </Link>
                <Link to={`/style/${document.id}`} className={cn(styles['railButton'])}>
                  <span className={cn(styles['railIcon'])} aria-hidden="true">
                    <SlidersHorizontal size={16} />
                  </span>
                  {t('documents:detail.action.style')}
                </Link>
                <button type="button" className={cn(styles['railButton'])} onClick={() => setShareDialogOpen(true)}>
                  <span className={cn(styles['railIcon'])} aria-hidden="true">
                    <Share2 size={16} />
                  </span>
                  {t('documents:detail.action.share')}
                </button>
              </>
            )}
          </div>

          {lastJobDone && (
            <div className={cn(styles['lastJob'])}>
              <p className={cn(styles['railSection'])}>{t('documents:job.lastJob.title')}</p>
              <div className={cn(styles['lastJobRow'])}>
                <span className={cn(styles['lastJobCheck'])} aria-hidden="true">
                  ✓
                </span>
                {t('documents:job.lastJob.complete')}
              </div>
              <p className={cn(styles['lastJobDetail'])}>{t('documents:job.lastJob.artifacts', { count: artifactCheckpoints.length })}</p>
            </div>
          )}

          <p className={cn(styles['railSection'])}>{t('documents:versions.title')}</p>
          {versionHistory.isLoading && (
            <div aria-busy="true">
              <Skeleton shape="row" count={3} />
            </div>
          )}
          {!versionHistory.isLoading && versionHistory.isError && (
            <p className={cn(styles['emptyNote'])}>{t('documents:versions.error')}</p>
          )}
          {!versionHistory.isLoading && !versionHistory.isError && versionHistory.versions.length === 0 && (
            <p className={cn(styles['emptyNote'])}>{t('documents:versions.empty')}</p>
          )}
          {!versionHistory.isLoading && !versionHistory.isError && versionHistory.versions.length > 0 && (
            <ul className={cn(styles['versionList'])}>
              {versionHistory.versions.map((v) => (
                <li key={v.id} className={cn(styles['versionRow'])}>
                  <span>{t(`documents:versions.origin.${v.origin}`)}</span>
                  <span className={cn(styles['versionTime'])}>{formatDate(locale, v.createdAt, { hour: '2-digit', minute: '2-digit' })}</span>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
